// src/components/PortfolioPage.js
import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { API_BASE_URL } from "../config/apiConfig";
import { 
  INDICATOR_OPTIONS,
  buildSeriesCache,
  getSeriesForIndicatorKey,
  rollingBands,
  buildFScoreTTMSeries,
} from "../indicators/engine";
import {
  getZScoreYearsForIndicator,
  isZScoreConfigurable,
  loadZScoreYearsMap,
  normalizeZScoreIndicatorKey,
} from "../indicators/zScoreConfig";
import { getFundamentals, getPrice, getAnalystConsensus, getAnalystForecasts, buildAnalystForecastsFromFundamentals } from "../services/api";
// Helper functions
const clamp = (x, lo, hi) => Math.max(lo, Math.min(hi, x));
function median(arr) { 
  if (!arr.length) return null; 
  const a = [...arr].sort((x, y) => x - y);
  const m = Math.floor(a.length / 2); 
  return a.length % 2 ? a[m] : (a[m - 1] + a[m]) / 2; 
}

// Highlight rules from StockChart - determines green/red regions
const GREEN_COLOR = "#22c55e"; // rgb(34, 197, 94)
const RED_COLOR = "#ef4444";   // rgb(239, 68, 68)

const HILITE_RULES = {
  ev_ebitda: [{ dir: "above", thr: 3, color: GREEN_COLOR, label: "EVY>3" }],
  ev_ebitda_z: [
    { dir: "above", thr: 1, color: GREEN_COLOR, label: "EV yield z>1" },
    { dir: "below", thr: -1, color: RED_COLOR, label: "EV yield z<-1" },
  ],
  div_yield_z: [
    { dir: "above", thr: 1, color: GREEN_COLOR, label: "Div yield z>1" },
    { dir: "below", thr: -1, color: RED_COLOR, label: "Div yield z<-1" },
  ],
  pb_ratio:   [ { dir:"below", thr:-1, color:GREEN_COLOR, label:"P/B z<-1 (DCA)" }, { dir:"above", thr:1, color:RED_COLOR, label:"P/B z>1 (Sell)" } ],
  pe_ratio:   [ { dir:"below", thr:-1, color:GREEN_COLOR, label:"P/E z<-1 (DCA)" }, { dir:"above", thr:1, color:RED_COLOR, label:"P/E z>1 (Sell)" } ],
  poi_ratio:   [ { dir:"below", thr:-1, color:GREEN_COLOR, label:"P/OI z<-1 (DCA)" }, { dir:"above", thr:1, color:RED_COLOR, label:"P/OI z>1 (Sell)" } ],
  ps_ratio:   [ { dir:"below", thr:-1,  color:GREEN_COLOR, label:"PS z<-1 (Below Avg)" }, { dir:"above", thr:1, color:RED_COLOR, label:"PS z>1 (Above Avg)" } ],
  cf_price:   [ { dir:"below", thr:-1, color:GREEN_COLOR, label:"P/CF z<-1 (DCA)" }, { dir:"above", thr:1, color:RED_COLOR, label:"P/CF z>1 (Sell)" } ],
  peg_ratio:  [ { dir:"below", thr:30,  color:GREEN_COLOR, label:"PEGpct<30" }, { dir:"above", thr:70, color:RED_COLOR, label:"PEGpct>70" } ],
  mean_rev:   [ { dir:"below", thr:-1,  color:GREEN_COLOR, label:"z<-1" }, { dir:"above", thr:1,  color:RED_COLOR,  label:"z>1" } ],
  syo:        [ { dir:"above", thr:50,  color:GREEN_COLOR, label:"SYO>50" }, { dir:"below", thr:50, color:RED_COLOR, label:"SYO<50" } ],
  rmo:        [ { dir:"above", thr:10,  color:GREEN_COLOR, label:"RMO>10" }, { dir:"below", thr:-10,color:RED_COLOR, label:"RMO<-10" } ],
  tamo:       [ { dir:"above", thr:30,  color:GREEN_COLOR, label:"TAMO>30" }, { dir:"below", thr:-30,color:RED_COLOR, label:"TAMO<-30" } ],
  nimo:       [ { dir:"above", thr:30,  color:GREEN_COLOR, label:"NIMO>30" }, { dir:"below", thr:-30,color:RED_COLOR, label:"NIMO<-30" } ],
  vmz:        [ { dir:"below", thr:0,   color:GREEN_COLOR, label:"VMZ<0" },  { dir:"above", thr:0,  color:RED_COLOR,  label:"VMZ>0" } ],
  oey:        [ { dir:"above", thr:70,  color:GREEN_COLOR, label:"OEY>70" }, { dir:"below", thr:30, color:RED_COLOR, label:"OEY<30" } ],
  vmo:        [ { dir:"below", thr:-0.5,color:GREEN_COLOR, label:"VMO<-0.5" }, { dir:"above", thr:0.5, color:RED_COLOR, label:"VMO>0.5" } ],
  mrvz:       [ { dir:"below", thr:-1.5,color:GREEN_COLOR, label:"MRVZ<-1.5" }, { dir:"above", thr:1.5, color:RED_COLOR, label:"MRVZ>1.5" } ],
  vddm:       [ { dir:"below", thr:-20, color:GREEN_COLOR, label:"VDDM<-20 (Buy)" }, { dir:"above", thr:20, color:RED_COLOR, label:"VDDM>20 (Sell)" } ],
  nupl:       [ { dir:"below", thr:-10, color:GREEN_COLOR, label:"NUPL<-10" }, { dir:"above", thr:10, color:RED_COLOR, label:"NUPL>10" } ],
  risk:       [ { dir:"below", thr:30,  color:GREEN_COLOR, label:"Risk<30" }, { dir:"above", thr:70, color:RED_COLOR, label:"Risk>70" } ],
  srm:        [ { dir:"above", thr:70,  color:GREEN_COLOR, label:"SRM>70" },  { dir:"below", thr:30, color:RED_COLOR, label:"SRM<30" } ],
  pe_peratio: [ { dir:"above", thr:1.2, color:GREEN_COLOR, label:"P/E Ratio>1.2" }, { dir:"below", thr:0.8, color:RED_COLOR, label:"P/E Ratio<0.8" } ],
};

// Determine color for an indicator value
function getIndicatorColor(indicatorKey, value) {
  if (value === null || value === undefined || !Number.isFinite(value)) {
    return null; // No color for invalid values
  }
  
  const rules = HILITE_RULES[normalizeZScoreIndicatorKey(indicatorKey)];
  if (!rules || !Array.isArray(rules)) {
    return null; // No rules for this indicator
  }
  
  // Check each rule - if value matches a green rule, return green; if red, return red
  // Priority: if multiple rules match, prefer green over red
  let hasGreen = false;
  let hasRed = false;
  
  for (const rule of rules) {
    const matches = rule.dir === "above" 
      ? value > rule.thr 
      : value < rule.thr;
    
    if (matches) {
      if (rule.color === GREEN_COLOR) {
        hasGreen = true;
      } else if (rule.color === RED_COLOR) {
        hasRed = true;
      }
    }
  }
  
  if (hasGreen) return GREEN_COLOR;
  if (hasRed) return RED_COLOR;
  return null; // No match - neutral color
}

// Basic columns that are always shown
const BASIC_COLS = [
  { key: "symbol", label: "Symbol", type: "str" },
  { key: "name", label: "Name", type: "str" },
  { key: "mcap", label: "Market Cap", type: "mcap" },
  { key: "last_price", label: "Last Price", type: "num", dp: 2 },
];

// Map backend keys to indicator keys
// NOTE: We only use backend data for indicators that don't need fresh computation
// Most indicators should be computed fresh using buildSeriesCache to match StockChart values
const BACKEND_KEY_MAP = {
  // Removed: syo, fmo (rmo), cfao, vmz, rsi_fd, pb_ratio, peg_ratio, ev_ebitda_yield, mean_rev_z, pe_dev
  // These need fresh computation to match StockChart values
  // Only keep indicators that are expensive to compute and don't change frequently
};

// Build indicator columns from INDICATOR_OPTIONS
const INDICATOR_COLS = Object.keys(INDICATOR_OPTIONS).map(key => {
  const backendKey = Object.keys(BACKEND_KEY_MAP).find(bk => BACKEND_KEY_MAP[bk] === key);
  const fromBackend = !!backendKey;
  
  const dp = 2;
  
  return {
    key,
    label: INDICATOR_OPTIONS[key],
    type: "num",
    dp,
    fromBackend,
    backendKey: backendKey || null,
  };
});

function fmt(v, type, dp = 2) {
  if (v === null || v === undefined || Number.isNaN(v)) return "—";
  if (type === "mcap") {
    const num = Number(v);
    if (num >= 1000) return `$${(num / 1000).toFixed(2)}T`;
    if (num >= 1) return `$${num.toFixed(2)}B`;
    return `$${(num * 1000).toFixed(2)}M`;
  }
  if (type === "num") return Number(v).toFixed(dp);
  return String(v);
}

const PORTFOLIO_STORAGE_KEY = "portfolio_stocks";

function getAuthHeaders() {
  const token = localStorage.getItem("authToken");
  return token ? { Authorization: `Bearer ${token}` } : {};
}

// Helper to merge unique symbol lists
function mergeUniqueSymbolLists(...lists) {
  const bySym = new Map();
  lists.flat().forEach(r => {
    if (!r || !r.symbol) return;
    const sym = String(r.symbol).toUpperCase();
    const name = r.name || r.companyName || "";
    if (!bySym.has(sym)) {
      bySym.set(sym, { symbol: sym, name });
    } else if (!bySym.get(sym).name && name) {
      bySym.set(sym, { symbol: sym, name });
    }
  });
  return Array.from(bySym.values()).sort((a, b) => a.symbol.localeCompare(b.symbol));
}

const portfolioZScoreYearsMap = loadZScoreYearsMap();

export default function PortfolioPage() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedIndicators, setSelectedIndicators] = useState(new Set());
  const [loadingIndicators, setLoadingIndicators] = useState(new Set());
  const [showIndicatorMenu, setShowIndicatorMenu] = useState(false);
  const [sortKey, setSortKey] = useState("symbol");
  const [sortDir, setSortDir] = useState("asc");
  const [newSymbol, setNewSymbol] = useState("");
  const [addingStock, setAddingStock] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [searchOptions, setSearchOptions] = useState([]);
  const [showSearchDropdown, setShowSearchDropdown] = useState(false);
  const [searchHighlight, setSearchHighlight] = useState(0);
  const searchBoxRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Load stock options for autocomplete
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const [nyseTsx, sp500] = await Promise.all([
          axios.get(`${API_BASE_URL}/tickers?ex=NYSE,NASDAQ,AMEX,TSX`),
          axios.get(`${API_BASE_URL}/sp500`),
        ]);
        if (mounted) {
          const merged = mergeUniqueSymbolLists(nyseTsx.data || [], sp500.data || []);
          setSearchOptions(merged);
        }
      } catch (err) {
        console.error('[PortfolioPage] Error fetching tickers:', err);
        if (mounted) setSearchOptions([]);
      }
    })();
    return () => { mounted = false; };
  }, []);

  // Filter search options based on input
  const filteredOptions = useMemo(() => {
    const q = newSymbol.trim().toLowerCase();
    if (!q) return searchOptions.slice(0, 20);
    return searchOptions
      .filter(o => o.symbol.toLowerCase().includes(q) || (o.name || "").toLowerCase().includes(q))
      .slice(0, 20);
  }, [newSymbol, searchOptions]);

  useEffect(() => setSearchHighlight(0), [newSymbol, showSearchDropdown]);

  // Load portfolio from API (per-user) or fallback to localStorage on mount
  useEffect(() => {
    let mounted = true;
    const token = localStorage.getItem("authToken");
    if (token) {
      axios.get(`${API_BASE_URL}/portfolio`, { headers: getAuthHeaders() })
        .then(({ data }) => {
          const symbols = data.symbols || [];
          if (mounted && symbols.length > 0) loadStocks(symbols);
        })
        .catch(() => {
          const saved = localStorage.getItem(PORTFOLIO_STORAGE_KEY);
          if (mounted && saved) {
            try {
              const symbols = JSON.parse(saved);
              if (Array.isArray(symbols) && symbols.length > 0) loadStocks(symbols);
            } catch (err) {
              console.error("Failed to load portfolio:", err);
            }
          }
        });
    } else {
      const saved = localStorage.getItem(PORTFOLIO_STORAGE_KEY);
      if (saved) {
        try {
          const symbols = JSON.parse(saved);
          if (Array.isArray(symbols) && symbols.length > 0) loadStocks(symbols);
        } catch (err) {
          console.error("Failed to load portfolio from localStorage:", err);
        }
      }
    }
    return () => { mounted = false; };
  }, []);

  // Save portfolio to API (per-user) or localStorage whenever it changes
  useEffect(() => {
    const symbols = rows.map(r => r.symbol).filter(Boolean);
    const token = localStorage.getItem("authToken");
    if (token) {
      axios.put(`${API_BASE_URL}/portfolio`, { symbols }, { headers: getAuthHeaders() }).catch(() => {
        localStorage.setItem(PORTFOLIO_STORAGE_KEY, JSON.stringify(symbols));
      });
    } else {
      localStorage.setItem(PORTFOLIO_STORAGE_KEY, JSON.stringify(symbols));
    }
  }, [rows]);

  // Load stock data for given symbols
  const loadStocks = async (symbols) => {
    setLoading(true);
    try {
      // Fetch screener data once for all stocks (for names and market cap info)
      let screenerData = [];
      try {
        const { data } = await axios.get(`${API_BASE_URL}/screener?ex=SP500`);
        screenerData = Array.isArray(data) ? data : [];
      } catch (err) {
        console.warn("Failed to fetch screener data:", err);
      }
      const screenerMap = new Map(screenerData.map(s => [s.symbol, s]));

      const stockPromises = symbols.map(async (symbol) => {
        try {
          const upperSymbol = symbol.toUpperCase();
          const screenerStock = screenerMap.get(upperSymbol);
          
          // Always fetch fresh price data
          const [priceData, fundamentals] = await Promise.all([
            getPrice(upperSymbol, { withVolume: false, timeseries: 30 }).catch(() => null),
            getFundamentals(upperSymbol).catch(() => null),
          ]);
          
          const lastPrice = priceData?.length ? priceData[priceData.length - 1]?.close ?? null : null;
          
          // Calculate market cap from shares outstanding and price
          let mcap = null;
          if (lastPrice && fundamentals?.income?.length > 0) {
            const latestIncome = fundamentals.income[0];
            const sharesOut = latestIncome?.weightedAverageShsOut || latestIncome?.weightedAverageShsOutDil;
            if (sharesOut) {
              mcap = (lastPrice * sharesOut) / 1000000000; // Convert to billions
            }
          }
          // Fallback to screener mcap if calculation failed
          if (mcap === null && screenerStock?.mcap) {
            mcap = screenerStock.mcap / 1000000000;
          }
          
          return {
            symbol: upperSymbol,
            name: screenerStock?.name || upperSymbol,
            mcap: mcap,
            last_price: lastPrice,
            _fullData: screenerStock || null,
          };
        } catch (err) {
          console.error(`Failed to load ${symbol}:`, err);
          return {
            symbol: symbol.toUpperCase(),
            name: symbol.toUpperCase(),
            mcap: null,
            last_price: null,
            _fullData: null,
          };
        }
      });

      const loadedStocks = await Promise.all(stockPromises);
      setRows(loadedStocks.filter(s => s.symbol));
      setLoading(false);
    } catch (err) {
      console.error("Failed to load stocks:", err);
      setLoading(false);
    }
  };

  // Add a new stock to portfolio
  const handleAddStock = async (selectedItem = null) => {
    const symbol = selectedItem?.symbol || newSymbol.trim().toUpperCase();
    const itemName = selectedItem?.name || symbol;
    if (!symbol) return;
    
    // Check if already exists
    if (rows.some(r => r.symbol === symbol)) {
      alert(`${symbol} is already in your portfolio`);
      setNewSymbol("");
      setShowSearchDropdown(false);
      return;
    }

    setAddingStock(true);
    setShowSearchDropdown(false);
    try {
      // Always fetch fresh price data and fundamentals
      const [priceData, fundamentals, screenerRes] = await Promise.all([
        getPrice(symbol, { withVolume: false, timeseries: 30 }).catch(() => null),
        getFundamentals(symbol).catch(() => null),
        axios.get(`${API_BASE_URL}/screener?ex=SP500`).catch(() => ({ data: [] })),
      ]);
      
      const screenerData = Array.isArray(screenerRes.data) ? screenerRes.data : [];
      const screenerStock = screenerData.find(s => s.symbol === symbol);
      
      const lastPrice = priceData?.length ? priceData[priceData.length - 1]?.close ?? null : null;
      
      // Calculate market cap from shares outstanding and price
      let mcap = null;
      if (lastPrice && fundamentals?.income?.length > 0) {
        const latestIncome = fundamentals.income[0];
        const sharesOut = latestIncome?.weightedAverageShsOut || latestIncome?.weightedAverageShsOutDil;
        if (sharesOut) {
          mcap = (lastPrice * sharesOut) / 1000000000; // Convert to billions
        }
      }
      // Fallback to screener mcap if calculation failed
      if (mcap === null && screenerStock?.mcap) {
        mcap = screenerStock.mcap / 1000000000;
      }
      
      const newRow = {
        symbol: symbol,
        name: screenerStock?.name || itemName,
        mcap: mcap,
        last_price: lastPrice,
        _fullData: screenerStock || null,
      };

      setRows(prev => [...prev, newRow]);
      setNewSymbol("");
    } catch (err) {
      console.error(`Failed to add ${symbol}:`, err);
      alert(`Failed to add ${symbol}. Please check if the symbol is valid.`);
    } finally {
      setAddingStock(false);
    }
  };

  // Handle keyboard navigation in search dropdown
  const handleSearchKeyDown = (e) => {
    if (!showSearchDropdown && (e.key === "ArrowDown" || e.key === "ArrowUp")) {
      setShowSearchDropdown(true);
      return;
    }
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setSearchHighlight(h => Math.min(h + 1, Math.max(filteredOptions.length - 1, 0)));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setSearchHighlight(h => Math.max(h - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (showSearchDropdown && filteredOptions.length) {
        handleAddStock(filteredOptions[searchHighlight]);
      } else if (newSymbol.trim()) {
        handleAddStock();
      }
    } else if (e.key === "Escape") {
      setShowSearchDropdown(false);
    }
  };

  // Remove a stock from portfolio
  const handleRemoveStock = (symbol) => {
    setRows(prev => prev.filter(r => r.symbol !== symbol));
  };

  // Load indicators when selected
  useEffect(() => {
    if (selectedIndicators.size === 0 || rows.length === 0) return;

    let mounted = true;
    const indicatorsToLoad = Array.from(selectedIndicators);
    const backendIndicators = indicatorsToLoad.filter(key => {
      const col = INDICATOR_COLS.find(c => c.key === key);
      return col?.fromBackend;
    });
    const frontendIndicators = indicatorsToLoad.filter(key => {
      const col = INDICATOR_COLS.find(c => c.key === key);
      return col && !col.fromBackend;
    });

    setLoadingIndicators(new Set(indicatorsToLoad));

    (async () => {
      try {
        // Load backend indicators from screener endpoint
        if (backendIndicators.length > 0 && mounted) {
          const { data } = await axios.get(`${API_BASE_URL}/screener?ex=SP500`);
          const fullData = Array.isArray(data) ? data : [];
          const dataMap = new Map(fullData.map(r => [r.symbol, r]));

          if (mounted) {
            setRows(prevRows =>
              prevRows.map(row => {
                const full = dataMap.get(row.symbol);
                if (!full) return row;
                const updated = { ...row };
                if (full.mcap !== undefined && full.mcap !== null && full.mcap > 0) {
                  updated.mcap = full.mcap / 1000000000;
                }
                backendIndicators.forEach(indicatorKey => {
                  const col = INDICATOR_COLS.find(c => c.key === indicatorKey);
                  const backendKey = col?.backendKey || indicatorKey;
                  const value = full[backendKey] ?? null;
                  updated[indicatorKey] = value;
                });
                return updated;
              })
            );
          }
        }

        // Load frontend indicators (computed from fundamentals)
        if (frontendIndicators.length > 0 && mounted) {
          const CONC = 4;
          const indicatorMap = new Map();

          for (let i = 0; i < rows.length; i += CONC) {
            if (!mounted) break;
            const chunk = rows.slice(i, i + CONC);
            const chunkRes = await Promise.all(
              chunk.map(async r => {
                try {
                  const [fundamentals, priceData] = await Promise.all([
                    getFundamentals(r.symbol),
                    getPrice(r.symbol, { withVolume: true, timeseries: 7300 }) // ~20 years to match StockChart
                  ]);

                  if (!fundamentals || !priceData) {
                    return { sym: r.symbol, indicators: {} };
                  }

                  // Sort price data chronologically (oldest first) and convert to required format
                  const sortedPriceData = Array.isArray(priceData) 
                    ? [...priceData].sort((a, b) => new Date(a.date) - new Date(b.date))
                    : [];
                  
                  const px = sortedPriceData.map(p => ({
                    time: new Date(p.date).getTime() / 1000,
                    value: p.close
                  })).filter(p => Number.isFinite(p.time) && Number.isFinite(p.value));

                  const fundamentalsWithBars = { ...fundamentals };
                  fundamentalsWithBars._dailyBars = sortedPriceData.map(p => ({
                    date: p.date,
                    close: p.close,
                    volume: p.volume || 0
                  }));

                  const needsAnalystForecasts = frontendIndicators.some((k) =>
                    ["forward_pe", "pe_peratio", "rev_val", "eps_val", "fgpz", "gag"].includes(k)
                  );
                  if (needsAnalystForecasts) {
                    try {
                      fundamentalsWithBars.analystForecasts = await getAnalystForecasts(
                        r.symbol,
                        fundamentalsWithBars.analystEstimates,
                        fundamentalsWithBars
                      );
                    } catch {
                      fundamentalsWithBars.analystForecasts = buildAnalystForecastsFromFundamentals(
                        r.symbol,
                        fundamentalsWithBars
                      );
                    }
                  }

                  // Use "daily" interval to match StockChart
                  const interval = "daily";
                  const lagDays = 0;
                  const cache = buildSeriesCache(fundamentalsWithBars, px, interval, lagDays);

                  const indicators = {};
                  frontendIndicators.forEach(indicatorKey => {
                    let series = null;
                    let value = null;

                    if (isZScoreConfigurable(indicatorKey)) {
                      series = getSeriesForIndicatorKey(
                        cache,
                        indicatorKey,
                        getZScoreYearsForIndicator(indicatorKey, portfolioZScoreYearsMap)
                      );
                    } else switch (indicatorKey) {
                      case "vmo":
                        series = cache.vmo;
                        break;
                      case "ev_ebitda":
                        series = cache.evYield;
                        break;
                      case "div_yield":
                        series = cache.divYield;
                        break;
                      case "peg_ratio":
                        series = cache.pegQ;
                        break;
                      case "syo":
                        series = cache.syo;
                        break;
                      case "rmo":
                        series = cache.rmo;
                        break;
                      case "tamo":
                        series = cache.tamo;
                        break;
                      case "nimo":
                        series = cache.nimo;
                        break;
                      case "vmz":
                        series = cache.vmz;
                        break;
                      case "oey":
                        series = cache.oey;
                        break;
                      case "ed":
                        series = cache.ed;
                        break;
                      case "mrvz":
                        series = cache.mrvz;
                        break;
                      case "vddm":
                        series = cache.vddm;
                        break;
                      case "nupl":
                        series = cache.nupl;
                        break;
                      case "realized_price_252":
                        series = cache.realizedPrice252;
                        break;
                      case "realized_price_504":
                        series = cache.realizedPrice504;
                        break;
                      case "realized_price_5y":
                        series = cache.realizedPrice5y;
                        break;
                      case "srm":
                        series = cache.srm;
                        break;
                      case "risk":
                        series = cache.risk;
                        break;
                      case "pe_peratio":
                        series = cache.pePERatio;
                        break;
                      case "forward_pe":
                        series = cache.forwardPe;
                        break;
                      case "ttm_pe":
                        series = cache.ttmPe;
                        break;
                      case "revenue_cagr":
                        series = cache.revenueCagr;
                        break;
                      case "eps_cagr":
                        series = cache.epsCagr;
                        break;
                      case "fgpz":
                        series = cache.fgpz;
                        break;
                      case "gag":
                        series = cache.gag;
                        break;
                      case "rev_val":
                        series = cache.revVal?.series || [];
                        break;
                      case "eps_val":
                        series = cache.epsVal?.series || [];
                        break;
                      case "mean_rev":
                        // Use rollingBands like StockChart
                        if (px.length >= 10) {
                          const stdWindow = 250;
                          const maxSigma = 3;
                          const rb = rollingBands(px, stdWindow, maxSigma);
                          series = px.map((p, i) => {
                            const sd = rb.sd[i]?.value || 0;
                            const mu = rb.mean[i]?.value || 0;
                            return { time: p.time, value: sd > 0 ? (p.value - mu) / sd : 0 };
                          });
                        }
                        break;
                      case "pe_dev":
                        // Compute pe_dev like StockChart using median over 5 years
                        if (px.length > 0) {
                          const epsSeriesPts = cache.eps || [];
                          const FIVE_Y = 5 * 365 * 24 * 3600;
                          const win = [];
                          const peDevSeries = px.map(bar => {
                            let i = -1;
                            for (let j = epsSeriesPts.length - 1; j >= 0; j--) {
                              if (epsSeriesPts[j].time <= bar.time) { i = j; break; }
                            }
                            const eps = (i >= 0) ? epsSeriesPts[i].value : null;
                            if (!(eps != null && isFinite(eps) && eps > 0)) return null;
                            const pe = bar.value / eps;
                            if (!isFinite(pe)) return null;
                            win.push({ time: bar.time, pe });
                            while (win.length && (bar.time - win[0].time) > FIVE_Y) win.shift();
                            const vals = win.map(x => x.pe);
                            const ref = median(vals);
                            if (!(ref != null && isFinite(ref) && ref > 0)) return null;
                            const devPct = clamp((pe / ref - 1) * 100, -100, 100);
                            return { time: bar.time, value: devPct };
                          }).filter(Boolean);
                          series = peDevSeries;
                        }
                        break;
                      default:
                        break;
                    }

                    if (series && Array.isArray(series) && series.length > 0) {
                      for (let j = series.length - 1; j >= 0; j--) {
                        const v = series[j]?.value;
                        if (Number.isFinite(v)) {
                          value = v;
                          break;
                        }
                      }
                    }

                    indicators[indicatorKey] = Number.isFinite(value) ? value : null;
                  });

                  return { sym: r.symbol, indicators };
                } catch (err) {
                  console.error(`Failed to compute indicators for ${r.symbol}:`, err);
                  return { sym: r.symbol, indicators: {} };
                }
              })
            );

            chunkRes.forEach(({ sym, indicators }) => {
              if (!indicatorMap.has(sym)) {
                indicatorMap.set(sym, new Map());
              }
              Object.entries(indicators).forEach(([key, value]) => {
                indicatorMap.get(sym).set(key, value);
              });
            });

            if (mounted) {
              setRows(prevRows =>
                prevRows.map(r => {
                  const symIndicators = indicatorMap.get(r.symbol);
                  if (!symIndicators) return r;
                  const updated = { ...r };
                  symIndicators.forEach((value, key) => {
                    if (updated[key] == null || updated[key] === undefined) {
                      updated[key] = value;
                    }
                  });
                  return updated;
                })
              );
            }
          }
        }
      } catch (err) {
        console.error("Failed to load indicators:", err);
      } finally {
        if (mounted) {
          setLoadingIndicators(new Set());
        }
      }
    })();

    return () => { mounted = false; };
  }, [selectedIndicators, rows.length]);

  // Get visible columns (basic + selected indicators)
  const visibleCols = useMemo(() => {
    const cols = [...BASIC_COLS];
    selectedIndicators.forEach(key => {
      const col = INDICATOR_COLS.find(c => c.key === key);
      if (col) cols.push(col);
    });
    return cols;
  }, [selectedIndicators]);

  // Sort stocks
  const sortedRows = useMemo(() => {
    const arr = [...rows];
    const col = visibleCols.find(c => c.key === sortKey);
    if (col) {
      const isNum = col.type === "num" || col.type === "mcap";
      arr.sort((a, b) => {
        const va = a?.[col.key];
        const vb = b?.[col.key];
        if (isNum) {
          const na = (va === null || va === undefined) ? (sortDir === "asc" ? Infinity : -Infinity) : Number(va);
          const nb = (vb === null || vb === undefined) ? (sortDir === "asc" ? Infinity : -Infinity) : Number(vb);
          return sortDir === "asc" ? na - nb : nb - na;
        }
        const sa = (va || "").toString();
        const sb = (vb || "").toString();
        return sortDir === "asc" ? sa.localeCompare(sb) : sb.localeCompare(sa);
      });
    }
    return arr;
  }, [rows, sortKey, sortDir, visibleCols]);

  const setSort = (key) => {
    if (key === sortKey) {
      setSortDir(d => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      const col = visibleCols.find(c => c.key === key);
      const isNum = col && (col.type === "num" || col.type === "mcap");
      setSortDir(isNum ? "desc" : "asc");
    }
  };

  const toggleIndicator = (key) => {
    setSelectedIndicators(prev => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    if (!showIndicatorMenu) return;
    const handleClickOutside = (event) => {
      const target = event.target;
      if (!target.closest('[data-indicator-menu]')) {
        setShowIndicatorMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showIndicatorMenu]);

  return (
    <div style={{ height: "100vh", display: "flex", flexDirection: "column" }}>
      {/* Top bar */}
      <div style={{
        padding: isMobile ? "8px 12px" : "10px 16px",
        borderBottom: "1px solid #e6e6e6",
        display: "flex",
        flexWrap: isMobile ? "wrap" : "nowrap",
        alignItems: "center",
        justifyContent: "space-between",
        gap: isMobile ? 8 : 0,
        position: "sticky",
        top: 0,
        background: "transparent",
        zIndex: 2
      }}>
        <div style={{ fontWeight: 700, fontSize: isMobile ? "16px" : "18px" }}>Portfolio</div>
        <div style={{ display: "flex", alignItems: "center", gap: isMobile ? 8 : 12, flexWrap: "wrap" }}>
          {/* Indicator selector */}
          <div style={{ position: "relative" }} data-indicator-menu>
            <button
              onClick={() => setShowIndicatorMenu(!showIndicatorMenu)}
              style={{
                border: "1px solid #d0d7de",
                background: "#fff",
                padding: isMobile ? "8px 12px" : "8px 16px",
                borderRadius: 8,
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                gap: 8,
                fontSize: isMobile ? "12px" : "14px",
                minHeight: "44px"
              }}
            >
              <span>{isMobile ? "Indicators" : "Select Indicators"}</span>
              {selectedIndicators.size > 0 && (
                <span style={{
                  background: "#0366d6",
                  color: "#fff",
                  borderRadius: "50%",
                  width: 20,
                  height: 20,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: 12,
                  fontWeight: 600
                }}>
                  {selectedIndicators.size}
                </span>
              )}
              <span>{showIndicatorMenu ? "▲" : "▼"}</span>
            </button>

            {/* Dropdown menu */}
            {showIndicatorMenu && (
              <div style={{
                position: "absolute",
                top: "100%",
                right: isMobile ? "auto" : 0,
                left: isMobile ? 0 : "auto",
                marginTop: 4,
                background: "#fff",
                border: "1px solid #d0d7de",
                borderRadius: 8,
                boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                padding: 8,
                minWidth: isMobile ? "calc(100vw - 24px)" : 320,
                maxWidth: isMobile ? "calc(100vw - 24px)" : "none",
                maxHeight: isMobile ? "60vh" : 500,
                overflowY: "auto",
                zIndex: 1000
              }}>
                <div style={{ padding: "8px 12px", fontWeight: 600, borderBottom: "1px solid #e6e6e6", marginBottom: 8, color: "#000" }}>
                  Select Indicators
                </div>
                <div style={{ maxHeight: 350, overflowY: "auto" }}>
                  {Object.entries({
                    "Novel Fundamental": ["syo", "rmo", "tamo", "nimo", "vmz", "vmo", "pe_peratio"],
                    "Classic Fundamental": ["rev_val", "eps_val", "ev_ebitda", "div_yield", "peg_ratio", "forward_pe", "ttm_pe", "revenue_cagr", "eps_cagr", "fgpz", "gag", "oey"],
                    "Classic / Novel Fundamental": ["div_yield_z", "pb_ratio", "pe_ratio", "poi_ratio", "ps_ratio", "cf_price", "ev_ebitda_z"],
                    "Price Action Based": ["mean_rev", "srm"],
                    "Novel Technical": ["mrvz", "vddm", "nupl", "realized_price_252", "realized_price_504", "realized_price_5y", "risk"],
                  }).map(([category, indicatorKeys]) => (
                    <div key={category} style={{ marginBottom: 12 }}>
                      <div style={{
                        fontWeight: 600,
                        marginBottom: 6,
                        color: "#000",
                        fontSize: 13,
                        paddingBottom: 4,
                        borderBottom: "1px solid #e6e6e6"
                      }}>
                        {category}
                      </div>
                      {indicatorKeys.map(key => {
                        const col = INDICATOR_COLS.find(c => c.key === key);
                        if (!col) return null;
                        const isSelected = selectedIndicators.has(col.key);
                        const isLoading = loadingIndicators.has(col.key);
                        return (
                          <label
                            key={col.key}
                            style={{
                              display: "flex",
                              alignItems: "center",
                              padding: "6px 12px",
                              cursor: "pointer",
                              borderRadius: 4,
                              background: isSelected ? "#f0f7ff" : "transparent",
                              opacity: isLoading ? 0.6 : 1,
                              marginLeft: 8
                            }}
                            onMouseEnter={(e) => {
                              if (!isLoading) e.currentTarget.style.background = isSelected ? "#e0efff" : "#f6f8fa";
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.background = isSelected ? "#f0f7ff" : "transparent";
                            }}
                          >
                            <input
                              type="checkbox"
                              checked={isSelected}
                              onChange={() => !isLoading && toggleIndicator(col.key)}
                              disabled={isLoading}
                              style={{ marginRight: 8, cursor: "pointer" }}
                            />
                            <span style={{ flex: 1, fontSize: 13, color: "#000" }}>{col.label}</span>
                            {isLoading && <span style={{ fontSize: 11, color: "#000" }}>Loading...</span>}
                          </label>
                        );
                      })}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          <button
            onClick={() => navigate("/")}
            style={{
              border: "1px solid #d0d7de",
              background: "#fff",
              padding: isMobile ? "8px 12px" : "8px 12px",
              borderRadius: 10,
              cursor: "pointer",
              fontSize: isMobile ? "12px" : "14px",
              minHeight: "44px"
            }}
          >
            Home
          </button>
        </div>
      </div>

      {/* Add Stock Section */}
      <div style={{
        padding: isMobile ? "8px 12px" : "12px 16px",
        borderBottom: "1px solid #e6e6e6",
        display: "flex",
        gap: 8,
        alignItems: "center",
        flexWrap: "wrap"
      }}>
        <div ref={searchBoxRef} style={{ position: "relative", flex: isMobile ? "1 1 100%" : "0 1 auto", minWidth: isMobile ? "120px" : "280px" }}>
          <input
            type="text"
            value={newSymbol}
            onChange={(e) => { setNewSymbol(e.target.value.toUpperCase()); setShowSearchDropdown(true); }}
            onFocus={() => setShowSearchDropdown(true)}
            onBlur={() => setTimeout(() => setShowSearchDropdown(false), 150)}
            onKeyDown={handleSearchKeyDown}
            placeholder="Search stock symbol (e.g., AAPL)"
            style={{
              border: "1px solid #d0d7de",
              padding: "8px 12px",
              borderRadius: 8,
              fontSize: isMobile ? "13px" : "14px",
              width: "100%",
              outline: "none"
            }}
          />
          {showSearchDropdown && filteredOptions.length > 0 && (
            <div style={{
              position: "absolute",
              top: "100%",
              left: 0,
              right: 0,
              marginTop: 4,
              background: "#fff",
              border: "1px solid #d0d7de",
              borderRadius: 8,
              boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
              maxHeight: 280,
              overflowY: "auto",
              zIndex: 1000
            }}>
              {filteredOptions.map((opt, i) => (
                <div
                  key={opt.symbol}
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => handleAddStock(opt)}
                  onMouseEnter={() => setSearchHighlight(i)}
                  style={{
                    padding: "10px 14px",
                    cursor: "pointer",
                    background: i === searchHighlight ? "#f0f7ff" : "transparent",
                    display: "flex",
                    alignItems: "baseline",
                    gap: 10,
                    borderLeft: i === searchHighlight ? "3px solid #0366d6" : "3px solid transparent",
                  }}
                >
                  <strong style={{ minWidth: 60, color: "#000", fontWeight: 600 }}>{opt.symbol}</strong>
                  <span style={{ color: "#666", fontSize: "13px" }}>{opt.name}</span>
                </div>
              ))}
            </div>
          )}
        </div>
        <button
          onClick={() => handleAddStock()}
          disabled={addingStock || !newSymbol.trim()}
          style={{
            border: "1px solid #d0d7de",
            background: addingStock || !newSymbol.trim() ? "#f6f8fa" : "#0366d6",
            color: addingStock || !newSymbol.trim() ? "#959da5" : "#fff",
            padding: "8px 16px",
            borderRadius: 8,
            cursor: addingStock || !newSymbol.trim() ? "not-allowed" : "pointer",
            fontSize: isMobile ? "13px" : "14px",
            fontWeight: 600,
            minHeight: "44px"
          }}
        >
          {addingStock ? "Adding..." : "Add Stock"}
        </button>
        {rows.length > 0 && (
          <span style={{ fontSize: isMobile ? "12px" : "13px", color: "#666", marginLeft: "auto" }}>
            {rows.length} {rows.length === 1 ? "stock" : "stocks"} in portfolio
          </span>
        )}
      </div>

      {/* Table */}
      <div style={{ padding: isMobile ? "8px" : "12px", overflow: "auto", flex: 1 }}>
        {rows.length === 0 ? (
          <div style={{ padding: 40, textAlign: "center", color: "#666", fontSize: isMobile ? "14px" : "16px" }}>
            Your portfolio is empty. Add stocks using the input above.
          </div>
        ) : (
          <div style={{ overflowX: "auto", WebkitOverflowScrolling: "touch" }}>
            <table style={{ borderCollapse: "collapse", width: "100%", minWidth: isMobile ? "800px" : "100%" }}>
              <thead style={{ background: "transparent" }}>
                <tr>
                  <th style={{
                    textAlign: "left",
                    padding: isMobile ? "6px 4px" : "8px",
                    borderBottom: "1px solid #e5e7eb",
                    whiteSpace: "nowrap",
                    fontSize: isMobile ? "11px" : "14px"
                  }}>
                    Actions
                  </th>
                  {visibleCols.map(c => (
                    <th
                      key={c.key}
                      onClick={() => setSort(c.key)}
                      style={{
                        textAlign: "left",
                        padding: isMobile ? "6px 4px" : "8px",
                        borderBottom: "1px solid #e5e7eb",
                        whiteSpace: "nowrap",
                        cursor: "pointer",
                        userSelect: "none",
                        fontSize: isMobile ? "11px" : "14px"
                      }}
                      title="Click to sort"
                    >
                      {c.label}
                      {sortKey === c.key ? (sortDir === "asc" ? " ▲" : " ▼") : ""}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {sortedRows.map((r, idx) => (
                  <tr key={`${r.symbol}-${idx}`} style={{ borderBottom: "1px solid #f0f0f0" }}>
                    <td style={{ padding: isMobile ? "6px 4px" : "8px", fontSize: isMobile ? "11px" : "14px" }}>
                      <button
                        onClick={() => handleRemoveStock(r.symbol)}
                        style={{
                          background: "transparent",
                          border: "1px solid #ef4444",
                          color: "#ef4444",
                          padding: "4px 8px",
                          borderRadius: 4,
                          cursor: "pointer",
                          fontSize: isMobile ? "11px" : "12px"
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.background = "#ef4444";
                          e.currentTarget.style.color = "#fff";
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.background = "transparent";
                          e.currentTarget.style.color = "#ef4444";
                        }}
                      >
                        Remove
                      </button>
                    </td>
                    {visibleCols.map(c => {
                      const value = r[c.key];
                      const isLoading = loadingIndicators.has(c.key) && (value === null || value === undefined);
                      const isIndicator = INDICATOR_COLS.some(col => col.key === c.key);
                      const indicatorColor = isIndicator ? getIndicatorColor(c.key, value) : null;
                      
                      return (
                        <td key={c.key} style={{ padding: isMobile ? "6px 4px" : "8px", fontSize: isMobile ? "11px" : "14px" }}>
                          {isLoading ? (
                            <span style={{ color: "#999", fontStyle: "italic" }}>Loading...</span>
                          ) : c.key === "symbol" ? (
                            <a
                              href="#"
                              onClick={(e) => { e.preventDefault(); navigate(`/stock/${r.symbol}/indicators`); }}
                              style={{ textDecoration: "none", color: "inherit", fontWeight: 700 }}
                            >
                              {r.symbol}
                            </a>
                          ) : (
                            <span style={{
                              color: indicatorColor || "inherit",
                              fontWeight: indicatorColor ? 600 : "normal"
                            }}>
                              {fmt(value, c.type, c.dp)}
                            </span>
                          )}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
